import { computed } from 'vue';

import i18n from '@/plugins/vue-i18n';
import { useCan } from '@/composables/use-can';
import { Actions, Subjects } from '@boqnet/permissions';
import { selectedOrgId } from '@/common/utils/role.utils';
import type { NavMenuItem } from '@/types/components/navigation.types';
import { BOQ_DATABASE_ROUTE_NAMES } from '@/router/boq-admin.routes';

import {
  CreditCardIcon,
  DocumentCheckIcon,
  DocumentTextIcon,
  HomeIcon,
  HomeModernIcon,
  ShoppingCartIcon,
  Squares2X2Icon,
} from '@heroicons/vue/24/outline';
import {
  CreditCardIcon as CreditCardIconSolid,
  DocumentCheckIcon as DocumentCheckIconSolid,
  DocumentTextIcon as DocumentTextIconSolid,
  HomeIcon as HomeIconSolid,
  HomeModernIcon as HomeModernIconSolid,
  ShoppingCartIcon as ShoppingCartIconSolid,
  Squares2X2Icon as Squares2X2IconSolid,
} from '@heroicons/vue/24/solid';

/**
 * Returns the `navItems` computed used by DefaultLayout and NavigationOnlyLayout.
 *
 * Each item's `visible` value is resolved via `useCan()` so it reacts to
 * permission changes at runtime. The action:subject pairs mirror those in
 * `navigation.truth-table.ts`.
 */
export function useNavItems() {
  const { t } = i18n.global;
  const { can } = useCan();

  const navItems = computed(
    () =>
      [
        {
          key: 'dashboard',
          label: t('routes.dashboardPageTitle'),
          icon: Squares2X2Icon,
          activeIcon: Squares2X2IconSolid,
          route: { name: 'dashboard', params: { organisationId: selectedOrgId.value } },
          visible: can(Actions.LIST, Subjects.ORG_DASHBOARD),
        },
        {
          key: 'client-dashboard',
          label: t('routes.clients.pageTitles.dashboard'),
          icon: Squares2X2Icon,
          activeIcon: Squares2X2IconSolid,
          route: {
            name: 'client-dashboard',
            params: { organisationId: selectedOrgId.value as string },
          },
          visible: false,
        },
        {
          key: 'client-variations',
          label: t('routes.clients.navigationLabel'),
          icon: DocumentCheckIcon,
          activeIcon: DocumentCheckIconSolid,
          route: {
            name: 'client-variations',
            params: { organisationId: selectedOrgId.value as string },
          },
          // CLIENT_DASHBOARD is granted unconditionally to CLI + PROF roles only —
          // using it avoids CASL type-level false-positives from conditional CLIENT_VARIATION rules.
          visible: can(Actions.READ, Subjects.CLIENT_DASHBOARD),
        },
        {
          key: 'projects',
          label: t('routes.projects.projectsPageTitle'),
          icon: HomeIcon,
          activeIcon: HomeIconSolid,
          route: {
            name: 'projects',
            params: { organisationId: selectedOrgId.value as string },
          },
          visible: can(Actions.READ, Subjects.PROJECT),
        },
        {
          key: 'orders',
          label: t('routes.orders.ordersPageTitle'),
          icon: ShoppingCartIcon,
          activeIcon: ShoppingCartIconSolid,
          route: {
            name: 'orders',
            params: { organisationId: selectedOrgId.value as string },
          },
          visible: can(Actions.LIST, Subjects.ORDER),
        },
        {
          key: 'claims',
          label: t('routes.orders.claimsPageTitle'),
          icon: DocumentTextIcon,
          activeIcon: DocumentTextIconSolid,
          route: {
            name: 'claims',
            params: { organisationId: selectedOrgId.value as string },
          },
          visible: can(Actions.LIST, Subjects.ORDER),
        },
        {
          key: 'accounts',
          label: t('routes.accounts.accountsPageTitle'),
          icon: CreditCardIcon,
          activeIcon: CreditCardIconSolid,
          route: {
            // Target the section entry (not a specific tab) so its role-based
            // redirect picks the right default tab — AP roles → Accounts Payable,
            // others → Payment Requests. Linking straight to 'accounts-payable'
            // would strand non-AP roles (e.g. SITE_FO) on the AP tab's
            // no-permission state. (PE-213)
            name: 'accounts',
            params: { organisationId: selectedOrgId.value as string },
          },
          // Accounts nav visibility is content-driven (PE-460): show only if the user can list
          // a real Accounts surface (PR / AP / Credits). A Site Worker has no PR/Payment view,
          // so the (now empty) Accounts entry is hidden; every other role still lists ≥1 surface.
          visible:
            can(Actions.LIST, Subjects.PAYMENT_REQUEST) ||
            can(Actions.LIST, Subjects.ACCOUNTS_PAYABLE) ||
            can(Actions.LIST, Subjects.CREDIT),
        },
        {
          key: 'vendors',
          label: t('routes.vendors.vendorsPageTitle'),
          icon: HomeModernIcon,
          activeIcon: HomeModernIconSolid,
          route: { name: 'vendors', params: { organisationId: selectedOrgId.value } },
          // The BOQ database is reached from Vendors, so its pages keep Vendors highlighted.
          alsoActiveOn: BOQ_DATABASE_ROUTE_NAMES,
          visible: can(Actions.READ, Subjects.ORG_VENDOR),
        },
      ] satisfies NavMenuItem[],
  );

  return { navItems };
}
